import { useState } from 'react'
import PageLayout from '../common/layout/PageLayout'
import adminToolsService from '../../services/AdminToolsService'

const AWS_ENV_LINKS = {
  test: {
    label: 'Open test env vars in AWS',
    href: 'https://us-east-1.console.aws.amazon.com/elasticbeanstalk/home?region=us-east-1#/environment/configuration/updates-monitoring-logging?environmentId=e-z7ktmm53my',
  },
  prod: {
    label: 'Open prod env vars in AWS',
    href: 'https://us-east-1.console.aws.amazon.com/elasticbeanstalk/home?region=us-east-1#/environment/configuration/updates-monitoring-logging?environmentId=e-3ri8gd9hpm',
  },
}

const ENABLEMENT_STEPS = [
  'Click the test AWS link below.',
  'In Elastic Beanstalk environment variables, add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_PHONE_NUMBER.',
  'Add TWILIO_WEBHOOK_URL if you want Twilio webhook callbacks in that environment.',
  'When you are ready for a real test text, set SMS_DRY_RUN=false and SMS_ENABLED=true, then save the AWS changes.',
  'In Admin Notification Controls, turn on the master switch and the SMS channel.',
  'Come back here and send yourself a test text.',
  'After test looks good, repeat the same steps with the prod AWS link.',
]

function AwsEnvLinks({ prefix = 'sms-tester' }) {
  return (
    <div
      className="d-flex flex-column flex-sm-row gap-2"
      data-testid={`${prefix}-aws-links`}
    >
      <a
        className="btn btn-outline-primary btn-sm"
        href={AWS_ENV_LINKS.test.href}
        target="_blank"
        rel="noreferrer"
        data-testid={`${prefix}-test-link`}
      >
        {AWS_ENV_LINKS.test.label}
      </a>
      <a
        className="btn btn-outline-secondary btn-sm"
        href={AWS_ENV_LINKS.prod.href}
        target="_blank"
        rel="noreferrer"
        data-testid={`${prefix}-prod-link`}
      >
        {AWS_ENV_LINKS.prod.label}
      </a>
    </div>
  )
}

function getErrorState(err) {
  const responseError = err?.response?.data?.error
  const fallbackMessage = err?.message || 'Failed to send test SMS'

  if (
    responseError?.includes('Missing required Twilio environment variables')
  ) {
    return {
      title: 'Twilio is not configured in this environment yet',
      message: responseError,
      helpText:
        'That is expected until you add the missing variables and turn on SMS sending.',
      showAwsLinks: true,
    }
  }

  return {
    title: 'Unable to send test SMS',
    message: responseError || fallbackMessage,
    helpText: '',
    showAwsLinks: false,
  }
}

function AdminSmsTester() {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  const handleSend = async () => {
    try {
      setSubmitting(true)
      setError('')
      setResult(null)

      if (!phoneNumber.trim()) {
        throw new Error('Phone number is required')
      }

      const response = await adminToolsService.sendTestSms({ phoneNumber })
      setResult(response)
    } catch (err) {
      setError(getErrorState(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PageLayout title="Admin SMS Tester">
      <div className="container-fluid py-3 py-md-4">
        <div className="row mb-3">
          <div className="col-12">
            <h1 className="h3 mb-1">Admin SMS Tester</h1>
            <p className="text-muted mb-0">
              Send a direct Twilio test message to a single phone number using
              the current server credentials.
            </p>
          </div>
        </div>

        {error && (
          <div className="row mb-3">
            <div className="col-12">
              <div
                className="alert alert-danger"
                role="alert"
                data-testid="sms-tester-error"
              >
                <div className="fw-semibold">{error.title}</div>
                <div>{error.message}</div>
                {error.helpText && (
                  <div className="small mt-1">{error.helpText}</div>
                )}
                {error.showAwsLinks && (
                  <div className="mt-3">
                    <AwsEnvLinks prefix="sms-tester-error" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="row g-3">
          <div className="col-12 col-md-5">
            <div className="card h-100">
              <div className="card-body">
                <h2 className="h5 mb-3">Send Test SMS</h2>

                <div className="mb-3">
                  <label htmlFor="smsTesterPhone" className="form-label">
                    Destination Phone Number
                  </label>
                  <input
                    id="smsTesterPhone"
                    type="tel"
                    className="form-control"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                    placeholder="+1 202 555 1234"
                    disabled={submitting}
                    data-testid="sms-tester-phone-input"
                  />
                  <div className="form-text">
                    Enter a US 10-digit number like 4046445636 or an
                    international number with country code. The server will
                    normalize and validate it before sending.
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSend}
                  disabled={submitting}
                  data-testid="sms-tester-send-button"
                >
                  {submitting ? 'Sending...' : 'Send Test SMS'}
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-7 d-flex flex-column gap-3">
            <div className="card">
              <div className="card-body">
                <h2 className="h5 mb-3">SMS Enablement Steps</h2>
                <p className="text-muted mb-3">
                  Do test first, then prod. These links go straight to the AWS
                  Elastic Beanstalk environment settings pages.
                </p>
                <div className="mb-3">
                  <AwsEnvLinks />
                </div>
                <ol className="mb-0 ps-3" data-testid="sms-tester-enable-steps">
                  {ENABLEMENT_STEPS.map((step) => (
                    <li key={step} className="mb-2">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className="card h-100">
              <div className="card-body">
                <h2 className="h5 mb-3">Last Result</h2>

                {!result && (
                  <div
                    className="text-muted"
                    data-testid="sms-tester-empty-result"
                  >
                    Send a test SMS to view the normalized number, message id,
                    and segment count.
                  </div>
                )}

                {result && (
                  <dl className="row mb-0" data-testid="sms-tester-result">
                    <dt className="col-sm-4">Phone Number</dt>
                    <dd className="col-sm-8">{result.data?.phoneNumber}</dd>

                    <dt className="col-sm-4">Message ID</dt>
                    <dd className="col-sm-8">{result.data?.messageId}</dd>

                    <dt className="col-sm-4">Segments</dt>
                    <dd className="col-sm-8">{result.data?.segmentCount}</dd>
                  </dl>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default AdminSmsTester
